import { useEffect } from 'react';

/**
 * SEO Component
 * Dynamically updates page title and meta tags for each page
 * Improves SEO and social media sharing
 * 
 * Usage:
 * <SEO 
 *   title="Page Title"
 *   description="Page description"
 *   keywords="keyword1, keyword2"
 *   canonical="https://www.lockitup.tech/page"
 *   ogImage="https://www.lockitup.tech/image.png"
 *   schema={schemaObject}
 * />
 */
function SEO({ 
  title, 
  description, 
  keywords, 
  ogTitle, 
  ogDescription, 
  ogUrl,
  ogImage,
  canonical,
  schema,
  article = false
}) {
  useEffect(() => {
    // Update page title
    if (title) {
      document.title = title;
    }

    // Update or create meta tag
    const updateMetaTag = (name, content, isProperty = false) => {
      if (!content) return;
      
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (element) {
        element.setAttribute('content', content);
      } else {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        element.setAttribute('content', content);
        document.head.appendChild(element);
      }
    };

    // Update or create link tag (for canonical)
    const updateLinkTag = (rel, href) => {
      if (!href) return;
      
      let element = document.querySelector(`link[rel="${rel}"]`);
      
      if (element) {
        element.setAttribute('href', href);
      } else {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        element.setAttribute('href', href);
        document.head.appendChild(element);
      }
    };

    // Update standard meta tags
    updateMetaTag('title', title);
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);

    // Update canonical URL
    if (canonical) {
      updateLinkTag('canonical', canonical);
    }

    // Update Open Graph tags
    updateMetaTag('og:title', ogTitle || title, true);
    updateMetaTag('og:description', ogDescription || description, true);
    updateMetaTag('og:url', ogUrl || canonical, true);
    updateMetaTag('og:type', article ? 'article' : 'website', true);
    
    if (ogImage) {
      updateMetaTag('og:image', ogImage, true);
      updateMetaTag('og:image:secure_url', ogImage, true);
      updateMetaTag('og:image:alt', title || 'lockitup', true);
    }

    // Update Twitter Card tags
    updateMetaTag('twitter:title', ogTitle || title);
    updateMetaTag('twitter:description', ogDescription || description);
    updateMetaTag('twitter:url', ogUrl || canonical);
    
    if (ogImage) {
      updateMetaTag('twitter:image', ogImage);
      updateMetaTag('twitter:image:alt', title || 'lockitup');
    }

    // Add or update structured data (JSON-LD)
    if (schema) {
      let scriptElement = document.getElementById('page-schema');
      
      if (!scriptElement) {
        scriptElement = document.createElement('script');
        scriptElement.id = 'page-schema';
        scriptElement.type = 'application/ld+json';
        document.head.appendChild(scriptElement);
      }
      
      scriptElement.textContent = JSON.stringify(schema);
    }

    // Cleanup function
    return () => {
      // Remove page-specific schema when unmounting
      const schemaElement = document.getElementById('page-schema');
      if (schemaElement) {
        schemaElement.remove();
      }
    };
  }, [title, description, keywords, ogTitle, ogDescription, ogUrl, ogImage, canonical, schema, article]);

  return null; // This component doesn't render anything
}

export default SEO;
